import React from 'react';
import { equipmentValidationPatterns, equipmentValidationMessages, badgeColors, shipmentStatusColors, billingStatusColors, legTypeColors, } from './constants';
import { BadgeAlt as Badge } from '@cargomatic/ui';
import { isNil } from 'lodash';
import { titleize, humanize } from 'underscore.string';
/**
 * Validates equipment number based on asset type
 * @param equipmentNumber - The equipment number to validate
 * @param assetType - The type of asset (Container, Chassis, etc.)
 * @returns Validation warning message or empty string if valid
 */
export function validateEquipmentNumber(equipmentNumber, assetType) {
    const assetTypeLower = assetType.toLowerCase();
    if (assetTypeLower === 'chassis') {
        if (!equipmentValidationPatterns.chassis.test(equipmentNumber)) {
            return equipmentValidationMessages.chassis;
        }
    }
    else if (assetTypeLower === 'container') {
        if (!equipmentValidationPatterns.container.test(equipmentNumber)) {
            return equipmentValidationMessages.container;
        }
    }
    return '';
}
/**
 * Gets all unique equipment numbers from shipments with their container types
 * @param shipments - Array of shipments
 * @returns Array of unique equipment with id and containerType
 */
export function getAllEquipmentFromShipments(shipments) {
    const equipmentMap = new Map();
    shipments.forEach((shipment) => {
        shipment.stops.forEach((stop) => {
            stop.equipmentIds.forEach((eq) => {
                if (!equipmentMap.has(eq.id)) {
                    equipmentMap.set(eq.id, eq.containerType);
                }
            });
        });
    });
    return Array.from(equipmentMap.entries()).map(([id, containerType]) => ({
        id,
        containerType,
    }));
}
/**
 * Checks if a shipment can be modified based on its status
 * @param status - The shipment status
 * @returns true if the shipment can be modified, false otherwise
 */
export function canModifyShipment(status) {
    return status !== 'Job Closed' && status !== 'Cancelled';
}
/**
 * Returns a status badge component with appropriate styling
 * @param status - The shipment status
 * @returns Badge component
 */
export function getStatusBadge(status) {
    const statusKey = status.toLowerCase().replace(' ', '_');
    const colorKey = shipmentStatusColors[statusKey] ||
        shipmentStatusColors.default;
    const colorClass = badgeColors[colorKey];
    return (React.createElement(Badge, { variant: "outline", className: `${colorClass} whitespace-nowrap` }, titleize(humanize(status))));
}
/**
 * Returns a billing status badge component with appropriate styling
 * @param status - The billing status
 * @returns Badge component or null if no status
 */
export function getBillingStatusBadge(status) {
    if (!status)
        return null;
    const statusKey = status.toLowerCase().replace(/\s+/g, '_');
    const colorKey = billingStatusColors[statusKey] ||
        billingStatusColors.default;
    const colorClass = badgeColors[colorKey];
    return (React.createElement(Badge, { variant: "outline", className: `${colorClass} whitespace-nowrap` }, titleize(humanize(status))));
}
/**
 * Returns a leg type badge component with appropriate styling
 * @param legType - The leg type
 * @returns Badge component or null if no leg type
 */
export function getLegTypeBadge(legType) {
    if (!legType)
        return null;
    const legTypeKey = legType
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace('-', '_');
    const colorKey = legTypeColors[legTypeKey] ||
        legTypeColors.default;
    const colorClass = badgeColors[colorKey];
    return (React.createElement(Badge, { variant: "outline", className: `${colorClass} whitespace-nowrap` }, humanize(legType)));
}
export function formatWeight(weigth, weightUnit) {
    if (isNil(weigth)) {
        return `—`;
    }
    const formatted = new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2,
    }).format(weigth);
    return `${formatted}${weightUnit ? ` ${weightUnit}` : ''}`;
}
export function formatStopAction(stop) {
    let action;
    if (stop.intent === 'deliver') {
        action = 'Delivery';
    }
    else {
        action = titleize(stop.intent);
    }
    if (stop.empty) {
        return `${action} empty`;
    }
    return action;
}
